import {
  type AsyncResult,
  createNotRequestedAsyncResult,
  createPendingAsyncResult,
  createResolvedAsyncResult,
} from "@/cache/AsyncResult";
import {
  type CacheDatum,
  CacheDatumState,
  type LoadedCacheDatum,
  type PermanentlyFailedCacheDatum,
} from "@/cache/CacheDatumState";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import { FailureState, type RetryState, createRetryState } from "@/cache/SpatioTemporalTileCache/RetryState";
import { type Stats, defaultCacheFormatter, jsxTable } from "@/cache/Stats";
import { evictCacheEntriesBySize, markCacheDatumUsed } from "@/cache/utils/cacheEviction";
import { createLoadedCacheDatum, sumLoadedCacheMemoryConsumption } from "@/cache/utils/cacheMemory";
import { KiB } from "@/utility/bytesize";

import { type InitialTimeRequest, type JSONInitDate, MeteomaticsApiUrl } from "@mm/api.meteomatics.com";
import type { MeteomaticsApiError } from "@mm/api.meteomatics.com";
import { type Abort, isAbortable } from "@mm/api.meteomatics.com";
import { cancelable } from "../utils";

export interface InitDateCacheStats {
  count: number;
  pending: number;
  estimatedMemoryConsumption: number;
}

export const emptyJSONInitDate: JSONInitDate = {
  //TODO think of something better
  parameter: "",
  model: "",
  initDate: "",
};

const DEFAULT_INIT_DATE_CACHE_MAX_SIZE = KiB(128);
const DEFAULT_INIT_DATE_CACHE_DELAY_EVICTION_SIZE = KiB(256);

export class InitDateCache implements Stats<InitDateCacheStats> {
  protected cache: { [key: string]: CacheDatum<JSONInitDate, RetryState> } = {};

  constructor(
    private readonly label_: string,
    private readonly maxSize: number = DEFAULT_INIT_DATE_CACHE_MAX_SIZE,
    private readonly delayEvictionUntilSize: number = DEFAULT_INIT_DATE_CACHE_DELAY_EVICTION_SIZE,
  ) {
    // Configuration-only constructor.
  }

  stats(): InitDateCacheStats {
    let count = 0;
    let pending = 0;
    const cache = this.cache;

    for (const line in cache) {
      if (Object.hasOwn(cache, line)) {
        count++;

        if (cache[line].state === CacheDatumState.Pending) {
          pending++;
        }
      }
    }

    return {
      count,
      pending,
      estimatedMemoryConsumption: sumLoadedCacheMemoryConsumption(cache),
    };
  }

  descJsx(): JSX.Element {
    return (
      <>Special-purpose cache for API initial time requests. Replacement policy is approximate memory-based LRU.</>
    );
  }

  label(): string {
    return this.label_;
  }

  clear() {
    this.cache = {};
  }

  private markUsed(cacheLine: LoadedCacheDatum<JSONInitDate>) {
    markCacheDatumUsed(cacheLine);
  }

  private unloadCacheEntry(_: CacheDatum<JSONInitDate, RetryState> | undefined, cacheId: string) {
    delete this.cache[cacheId];
  }

  private pruneCacheEntries() {
    evictCacheEntriesBySize(this.cache, this.maxSize, this.delayEvictionUntilSize, this.unloadCacheEntry.bind(this));
  }

  /**
   * Couples local promise cancelation with abort dispatch in the API worker for the same request URL.
   * The callback is idempotent to tolerate repeated `cancel()` calls from callers.
   */
  private createCancelableWithAbort(cacheId: string, cancel: () => void, requestUrls: string[]): () => void {
    let canceled = false;
    const dedupUrls = Array.from(new Set(requestUrls)).filter((url) => typeof url === "string" && url.length > 0);

    return () => {
      if (canceled) {
        return;
      }
      canceled = true;
      cancel();
      if (dedupUrls.length) {
        apiThreadPool.abortRequestByUrl(dedupUrls);
      }
      delete this.cache[cacheId];
    };
  }

  statsJsx(): JSX.Element {
    return jsxTable(this.stats(), defaultCacheFormatter);
  }

  /**
   * A method to return a cached data from cache.
   * It does not trigger a new request when the given request hasn't been emitted.
   * @param request
   * @param kind
   * @returns
   */
  peekCache(cacheId: string): AsyncResult<JSONInitDate> {
    const v = this.cache[cacheId];
    if (!v) {
      return createNotRequestedAsyncResult(emptyJSONInitDate);
    }
    switch (v.state) {
      case CacheDatumState.Pending: {
        const retry_ = v.payload;
        if (retry_.failureState !== FailureState.FailedTemporarily) {
          return createPendingAsyncResult(v.promise);
        }
        // since the current invocation moves the state from [Pending/FailedTemporarily] to [Pending/Queued]
        // mark it as such to prevent concurrent invocations from reentering the remaining function body
        retry_.failureState = undefined;
        return createPendingAsyncResult(v.promise);
      }
      case CacheDatumState.PermanentlyFailed:
        return createResolvedAsyncResult(emptyJSONInitDate);
      case CacheDatumState.Loaded:
        this.markUsed(v);
        return createResolvedAsyncResult(v.payload);
      default: {
        const _exhaustive: never = v;
        return _exhaustive;
      }
    }
  }

  retrieveInitDate(request: InitialTimeRequest): AsyncResult<JSONInitDate> {
    const cacheId = `initDate${request.parameters[0]}${request.model}${request.validDate}`;

    if (Object.hasOwn(this.cache, cacheId)) {
      return this.peekCache(cacheId);
    }

    const retry_: RetryState | null = null;
    const retry = retry_ ?? createRetryState();
    const requestDatetime = performance.now();
    // Build exactly the URL string used by the API transport so abort matching is reliable.
    const requestUrl = MeteomaticsApiUrl.forInitialTime(
      request.model,
      request.parameters,
      request.validDate,
    ).toString();
    const cancelablePromise = cancelable(
      apiThreadPool
        .getInitTime(request)
        .then((data) => {
          let result: any;
          if (data.initTimes[0].initTime.includes("00-00-00")) {
            result = {
              parameter: request.parameters[0],
              model: request.model,
              initDate: "init time not assignable to requested time",
            };
          } else {
            result = { parameter: request.parameters[0], model: request.model, initDate: data.initTimes[0].initTime };
          }

          const cacheLine = this.cache[cacheId];
          // Ignore stale completions from canceled/replaced requests.
          if (
            !cacheLine ||
            cacheLine.state !== CacheDatumState.Pending ||
            cacheLine.requestDatetime !== requestDatetime
          ) {
            return result;
          }

          const cacheData: LoadedCacheDatum<JSONInitDate> = createLoadedCacheDatum(result, requestDatetime);
          this.cache[cacheId] = cacheData;
          this.markUsed(cacheData);
          this.pruneCacheEntries();
          return result;
        })
        .catch((e: MeteomaticsApiError | Abort) => {
          if (isAbortable(e)) {
            const cacheLine = this.cache[cacheId];
            if (
              cacheLine &&
              cacheLine.state === CacheDatumState.Pending &&
              cacheLine.requestDatetime === requestDatetime
            ) {
              delete this.cache[cacheId];
            }
            return Promise.reject(e);
          }
          return this.reactOnMeteomaticsApiError(e, retry, cacheId, requestDatetime);
        }),
    );
    const cancel = this.createCancelableWithAbort(cacheId, cancelablePromise.cancel, [requestUrl]);

    this.cache[cacheId] = {
      requestDatetime,
      state: CacheDatumState.Pending,
      promise: cancelablePromise.promise,
      cancel,
      payload: retry,
    };
    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  /**
   * Marks matching pending request as permanently failed; ignores stale completions.
   */
  reactOnMeteomaticsApiError(
    e: MeteomaticsApiError,
    _retry: RetryState,
    cacheId: string,
    requestDatetime: number,
  ): Promise<never> {
    const cacheLine = this.cache[cacheId];
    if (!cacheLine || cacheLine.state !== CacheDatumState.Pending || cacheLine.requestDatetime !== requestDatetime) {
      return Promise.reject(e);
    }

    switch (e.kind) {
      case "BoundsError":
      case "TimeError":
      case "ComputationTimeoutError":
      case "AuthError":
      case "UnknownError":
      case "UnknownParameterError":
      case "MissingDataError": {
        const cacheData: PermanentlyFailedCacheDatum<null> = {
          requestDatetime,
          state: CacheDatumState.PermanentlyFailed,
          payload: null,
        };
        this.cache[cacheId] = cacheData;
        return Promise.reject(e);
      }
      default: {
        const _exhaustive: never = e;
        return _exhaustive;
      }
    }
  }
}
